import React, { useMemo, useState } from "react";
import { CalendarDays, Clock, Lock, Plus, ShieldCheck, Tag } from "lucide-react";
import type { DiscountCampaign } from "../types/discount";
import { formatDiscountDateTime } from "../hooks/useActiveDiscount";

type Status = "idle" | "loading" | "success" | "error";

const toDateTimeLocal = (date: Date) => {
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
};

const now = new Date();
const defaultEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

function toIso(value: string) {
    return new Date(value).toISOString();
}

function getCampaignState(campaign: DiscountCampaign) {
    const start = new Date(campaign.startAt).getTime();
    const end = new Date(campaign.endAt).getTime();
    const current = Date.now();

    if (!campaign.enabled) return "Paused";
    if (current < start) return "Scheduled";
    if (current > end) return "Expired";
    return "Live";
}

function getStateClass(state: string) {
    if (state === "Live") return "bg-emerald-100 text-emerald-800";
    if (state === "Scheduled") return "bg-blue-100 text-blue-800";
    if (state === "Expired") return "bg-slate-200 text-slate-700";
    return "bg-amber-100 text-amber-800";
}

function groupCampaignsByMonth(campaigns: DiscountCampaign[]) {
    return campaigns.reduce<Record<string, DiscountCampaign[]>>((groups, campaign) => {
        const date = new Date(campaign.startAt);
        const key = Number.isNaN(date.getTime())
            ? "Unscheduled"
            : date.toLocaleString("en-IN", { month: "long", year: "numeric" });
        groups[key] = groups[key] || [];
        groups[key].push(campaign);
        return groups;
    }, {});
}

export default function DiscountAdmin() {
    const [password, setPassword] = useState("");
    const [campaigns, setCampaigns] = useState<DiscountCampaign[]>([]);
    const [status, setStatus] = useState<Status>("idle");
    const [message, setMessage] = useState("");
    const [name, setName] = useState("");
    const [percent, setPercent] = useState("30");
    const [startAt, setStartAt] = useState(toDateTimeLocal(now));
    const [endAt, setEndAt] = useState(toDateTimeLocal(defaultEnd));
    const [enabled, setEnabled] = useState(true);

    const groupedCampaigns = useMemo(() => groupCampaignsByMonth(campaigns), [campaigns]);

    async function loadCampaigns() {
        setStatus("loading");
        setMessage("");

        try {
            const response = await fetch("/api/discounts?admin=1", {
                headers: { "x-admin-password": password },
                cache: "no-store",
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Unable to load discounts.");
            }

            setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
            setMessage(typeof data.warning === 'string' ? data.warning : '');
            setStatus("success");
        } catch (error) {
            setStatus("error");
            setMessage(error instanceof Error ? error.message : "Unable to load discounts.");
        }
    }

    async function createCampaign(event: React.FormEvent) {
        event.preventDefault();
        setStatus("loading");
        setMessage("");

        try {
            const response = await fetch("/api/discounts", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-admin-password": password,
                },
                body: JSON.stringify({
                    name,
                    percent: Number(percent),
                    startAt: toIso(startAt),
                    endAt: toIso(endAt),
                    enabled,
                }),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Unable to save discount.");
            }

            setName("");
            setMessage("Discount saved. It will appear publicly during the selected window.");
            await loadCampaigns();
        } catch (error) {
            setStatus("error");
            setMessage(error instanceof Error ? error.message : "Unable to save discount.");
        }
    }

    const isUnlocked = campaigns.length > 0 || status === "success";

    return (
        <main className="min-h-screen bg-[#f7f3ee] text-slate-900">
            <section className="mx-auto max-w-6xl px-5 py-10 md:py-14">
                <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div>
                        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-200">
                            <ShieldCheck size={14} />
                            Client Access
                        </div>
                        <h1 className="text-3xl font-bold md:text-5xl">Discount Calendar</h1>
                        <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
                            Schedule service discounts by name, percentage, start date/time, and end date/time. Active discounts update the public offer banner and service prices automatically.
                        </p>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[390px_1fr]">
                    <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="mb-5 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-amber-200">
                                <Lock size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold">Owner Password</h2>
                                <p className="text-sm text-slate-500">Required before viewing or saving discounts.</p>
                            </div>
                        </div>

                        <label className="block text-sm font-semibold text-slate-700" htmlFor="discount-password">
                            Password
                        </label>
                        <input
                            id="discount-password"
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                            placeholder="Enter private password"
                        />
                        <button
                            type="button"
                            onClick={loadCampaigns}
                            disabled={!password || status === "loading"}
                            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <CalendarDays size={18} />
                            Open Calendar
                        </button>

                        {message && (
                            <p className={`mt-4 rounded-xl px-4 py-3 text-sm ${status === "error" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                                {message}
                            </p>
                        )}
                    </aside>

                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="mb-5 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-200 text-slate-950">
                                <Tag size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold">Create Discount</h2>
                                <p className="text-sm text-slate-500">Use future dates to schedule campaigns in advance.</p>
                            </div>
                        </div>

                        <form onSubmit={createCampaign} className="grid gap-4 md:grid-cols-2">
                            <label className="md:col-span-2">
                                <span className="text-sm font-semibold text-slate-700">Discount Name</span>
                                <input
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                                    placeholder="Example: Navratri Blessings Offer"
                                    required
                                />
                            </label>

                            <label>
                                <span className="text-sm font-semibold text-slate-700">Discount Percentage</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="90"
                                    value={percent}
                                    onChange={(event) => setPercent(event.target.value)}
                                    className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                                    required
                                />
                            </label>

                            <label className="flex items-end gap-3 rounded-xl border border-slate-200 px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={enabled}
                                    onChange={(event) => setEnabled(event.target.checked)}
                                    className="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                />
                                <span>
                                    <span className="block text-sm font-semibold text-slate-700">Enabled</span>
                                    <span className="block text-xs text-slate-500">Show publicly during selected dates.</span>
                                </span>
                            </label>

                            <label>
                                <span className="text-sm font-semibold text-slate-700">From Date & Time</span>
                                <input
                                    type="datetime-local"
                                    value={startAt}
                                    onChange={(event) => setStartAt(event.target.value)}
                                    className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                                    required
                                />
                            </label>

                            <label>
                                <span className="text-sm font-semibold text-slate-700">End Date & Time</span>
                                <input
                                    type="datetime-local"
                                    value={endAt}
                                    onChange={(event) => setEndAt(event.target.value)}
                                    className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                                    required
                                />
                            </label>

                            <button
                                type="submit"
                                disabled={!password || status === "loading"}
                                className="md:col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-300 px-4 py-3 text-sm font-bold text-slate-950 transition hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <Plus size={18} />
                                Save Discount
                            </button>
                        </form>
                    </section>
                </div>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-5 flex items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-bold">Scheduled Calendar</h2>
                            <p className="text-sm text-slate-500">All saved discounts appear by their start month.</p>
                        </div>
                        <Clock className="text-slate-400" size={22} />
                    </div>

                    {!isUnlocked ? (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                            Enter the private password and open the calendar to view scheduled discounts.
                        </div>
                    ) : campaigns.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                            No discounts scheduled yet.
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {Object.entries(groupedCampaigns).map(([month, monthCampaigns]) => (
                                <div key={month}>
                                    <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-slate-500">{month}</h3>
                                    <div className="grid gap-3 md:grid-cols-2">
                                        {monthCampaigns.map((campaign) => {
                                            const state = getCampaignState(campaign);
                                            const day = new Date(campaign.startAt).toLocaleDateString("en-IN", { day: "2-digit" });

                                            return (
                                                <article key={campaign.id} className="rounded-xl border border-slate-200 p-4">
                                                    <div className="flex items-start gap-4">
                                                        <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-slate-100 text-slate-900">
                                                            <span className="text-xl font-black">{day}</span>
                                                            <span className="text-[10px] font-bold uppercase text-slate-500">Start</span>
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                                                <h4 className="font-bold text-slate-900">{campaign.name}</h4>
                                                                <span className={`rounded-full px-2 py-1 text-xs font-bold ${getStateClass(state)}`}>
                                                                    {state}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm font-semibold text-amber-700">{campaign.percent}% off</p>
                                                            <p className="mt-2 text-xs leading-5 text-slate-500">
                                                                From {formatDiscountDateTime(campaign.startAt)}
                                                                <br />
                                                                To {formatDiscountDateTime(campaign.endAt)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </section>
        </main>
    );
}
